import { StyleSheet, View } from 'react-native';
import { COLOR, FONT_SIZE, RADIUS, SPACING } from 'constants/design-system';

import { TAB_BARS } from 'store/App/constants';
import { useAppSelector } from 'store/hooks';
import { OrderDeliveryChannels } from 'store/Orders/enums';
import { SettingSelectors } from 'store/Settings';
import { UserSelectors } from 'store/User';
import { getIconNameForOrderKind } from 'util/helpers';
import { getDriverPictureURL } from 'util/helpers/cloudinary';
import { useNavigationContext } from 'util/hooks/useNavigationContext';
import { BaseIcon } from 'components/BaseIcon';
import { CountDownLabel } from 'components/CountDownLabel';
import DriverAvatar from 'components/DriverAvatar';
import WebImage from 'components/WebImage';

import { useBadgeSectionDisplaySettings } from './useBadgeSectionDisplaySettings';
import type { Order } from 'store/Orders/types';

export type BadgeSectionProps = {
  order: Order;
  isAllHeaderOptionVisible?: boolean;
};
export const BadgeSection = (props: BadgeSectionProps) => {
  const { order, isAllHeaderOptionVisible } = props;

  const { activeRouteKey } = useNavigationContext();

  const {
    shouldDisplayDriverDetails,
    shouldDisplayOrderChannel,
    shouldDisplayOrderType,
  } = useBadgeSectionDisplaySettings({
    ignoreDeviceSetting: isAllHeaderOptionVisible,
  });

  return (
    <View style={{ flexDirection: 'row', gap: SPACING.theme[4] }}>
      {shouldDisplayOrderType && (
        <View style={styles.itemContainer}>
          <BaseIcon
            color={COLOR.theme.white}
            name={getIconNameForOrderKind(order.kind)}
            variant="material-community"
            size={FONT_SIZE.theme['3xl']}
          />
        </View>
      )}

      {shouldDisplayOrderChannel &&
        order.channel &&
        order.channel.slug !== OrderDeliveryChannels.UNKNOWN && (
          <WebImage
            uri={order.channel.logo_cloudinary_url}
            width={48}
            height={48}
            shape={'rect'}
            containerStyle={[styles.itemContainer, { width: 'auto' }]}
          />
        )}

      {shouldDisplayDriverDetails && order.assigned_driver && (
        <DriverAvatar
          uri={getDriverPictureURL(order.assigned_driver.profile_picture_id)}
          fullName={order.assigned_driver.full_name}
          size={48}
          style={[styles.itemContainer, { width: 48 }]}
        />
      )}

      {order.latest_pickup_time &&
        (
          [
            TAB_BARS.new.value,
            TAB_BARS.cooking.value,
            TAB_BARS.scheduled.value,
            TAB_BARS.newAndCooking.value,
          ] as unknown[]
        ).includes(activeRouteKey) && (
          <CountDownLabel
            latestPickupTime={order.latest_pickup_time}
            style={[
              styles.itemContainer,
              {
                // TODO: remove from default
                marginTop: 0,
              },
            ]}
          />
        )}
    </View>
  );
};

const styles = StyleSheet.create({
  itemContainer: {
    alignItems: 'center',
    borderColor: COLOR.theme.white,
    borderRadius: RADIUS.theme.md,
    borderWidth: 1,
    flexBasis: 48,
    flexShrink: 1,
    height: 48,
    justifyContent: 'center',
  },
});
